import { useState } from "react";
import {
    Button,
    Card,
    CardContent,
    Container,
    Box,
    Typography,
    TextField,
    InputAdornment,
    Alert,
    CircularProgress,
    Link as MuiLink,
} from "@mui/material";
import { useNavigate, useSearchParams } from "react-router-dom";
import EmailIcon from "@mui/icons-material/Email";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { APP_IMAGES } from "../../constants/AppImages";
import { HTTP_STATUS, ROUTES, intl } from "dmsbasereact";
import { APP_COLORS } from "../../theme/appTheme";
import { fetchData } from "dmsbasereact";

export const RegisterPage = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    const [email, setEmail] = useState(searchParams.get("email") || "");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);

    const handleRegisterSubmit = async (e) => {
        e.preventDefault();
        setError("");

        if (!email) {
            setError(intl.get("PleaseFillAllFields").d("Please fill in all fields"));
            return;
        }

        // Email validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) {
            setError(intl.get("InvalidEmailFormat").d("Invalid email format"));
            return;
        }

        setLoading(true);

        try {
            const res = await fetchData(
                "/email/send-verification",
                "POST",
                false,
                { email: email },
            );
            if (res.status === HTTP_STATUS.OK_200) {
                setSuccess(true);
            }

            return res;
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Container
            className="register-page"
            maxWidth="false"
            disableGutters
            sx={{
                background: "#FFFFFF",
                minHeight: "100vh",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                position: "relative",
                overflow: "hidden",
            }}
        >
            <Box
                sx={{
                    position: "relative",
                    zIndex: 1,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    width: "100%",
                    maxWidth: "500px",
                    px: 2,
                }}
            >
                {/* Logo Section */}
                <Box
                    sx={{
                        mb: 4,
                        textAlign: "center",
                    }}
                >
                    <Box
                        component="img"
                        src={APP_IMAGES.LOGO}
                        alt="Lena Logo"
                        sx={{
                            width: "150px",
                            height: "auto",
                            mb: 2,
                            filter: "drop-shadow(2px 2px 8px rgba(0,0,0,0.3))",
                        }}
                    />
                </Box>

                {/* Register Card */}
                <Card
                    sx={{
                        width: "100%",
                        boxShadow: "0 8px 32px rgba(0, 0, 0, 0.3)",
                        borderRadius: 3,
                    }}
                >
                    <CardContent sx={{ p: 4 }}>
                        {success ? (
                            // Success State
                            <Box>
                                <Typography
                                    variant="h5"
                                    sx={{
                                        textAlign: "center",
                                        mb: 2,
                                        fontWeight: 600,
                                        color: APP_COLORS.neutral.gray900,
                                    }}
                                >
                                    {intl.get("RegistrationSuccessful").d("Check your inbox")}
                                </Typography>

                                <Alert severity="info" sx={{ mb: 3 }}>
                                    {intl.get("VerificationEmailSent").d("If this email is not yet registered, you will receive a verification link shortly. Please check your inbox.")}
                                </Alert>

                                <Button
                                    fullWidth
                                    variant="contained"
                                    onClick={() => navigate(ROUTES.Login)}
                                    startIcon={<ArrowBackIcon />}
                                    sx={{
                                        backgroundColor: APP_COLORS.primary.main,
                                        color: APP_COLORS.primary.contrastText,
                                        height: "50px",
                                        textTransform: "none",
                                        fontSize: "16px",
                                        fontWeight: "bold",
                                        borderRadius: 2,
                                        "&:hover": {
                                            backgroundColor: APP_COLORS.primary.dark,
                                        },
                                    }}
                                >
                                    {intl.get("BackToLogin").d("Back to Login")}
                                </Button>
                            </Box>
                        ) : (
                            // Registration Form
                            <>
                                <Typography
                                    variant="h5"
                                    sx={{
                                        textAlign: "center",
                                        mb: 1,
                                        fontWeight: 600,
                                        color: APP_COLORS.neutral.gray900,
                                    }}
                                >
                                    {intl.get("CreateAccount").d("Create Account")}
                                </Typography>

                                <Typography
                                    variant="body2"
                                    sx={{
                                        textAlign: "center",
                                        mb: 3,
                                        color: APP_COLORS.neutral.gray600,
                                    }}
                                >
                                    {intl.get("RegisterSubtitle").d("Sign up to get started")}
                                </Typography>

                                {/* Error Alert */}
                                {error && (
                                    <Alert severity="error" sx={{ mb: 2 }}>
                                        {error}
                                    </Alert>
                                )}

                                {/* Register Form */}
                                <form onSubmit={handleRegisterSubmit}>

                                    {/* Email */}
                                    <Box mb={2}>
                                        <TextField
                                            required
                                            fullWidth
                                            id="email"
                                            name="email"
                                            label={intl.get("EmailAddress").d("Email Address")}
                                            type="email"
                                            value={email}
                                            onChange={(e) => setEmail(e.target.value)}
                                            disabled={loading}
                                            variant="outlined"
                                            InputProps={{
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <EmailIcon sx={{ color: APP_COLORS.neutral.gray600 }} />
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />
                                    </Box>

                                    {/* Submit Button */}
                                    <Button
                                        type="submit"
                                        fullWidth
                                        variant="contained"
                                        disabled={loading}
                                        sx={{
                                            backgroundColor: APP_COLORS.primary.main,
                                            color: APP_COLORS.primary.contrastText,
                                            height: "50px",
                                            textTransform: "none",
                                            fontSize: "16px",
                                            fontWeight: "bold",
                                            borderRadius: 2,
                                            mb: 2,
                                            "&:hover": {
                                                backgroundColor: APP_COLORS.primary.dark,
                                            },
                                        }}
                                    >
                                        {loading ? (
                                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                <CircularProgress size={20} color="inherit" />
                                                <span>{intl.get("CreatingAccount").d("Creating Account...")}</span>
                                            </Box>
                                        ) : (
                                            intl.get("CreateAccount").d("Create Account")
                                        )}
                                    </Button>

                                    {/* Back to Login Link */}
                                    <Box textAlign="center">
                                        <Typography variant="body2" sx={{ color: APP_COLORS.neutral.gray600 }}>
                                            {intl.get("AlreadyHaveAccount").d("Already have an account?")}{" "}
                                            <MuiLink
                                                component="button"
                                                type="button"
                                                variant="body2"
                                                onClick={() => navigate(ROUTES.Login)}
                                                sx={{
                                                    textDecoration: "none",
                                                    color: APP_COLORS.primary.main,
                                                    fontWeight: 600,
                                                    "&:hover": {
                                                        textDecoration: "underline",
                                                    },
                                                }}
                                            >
                                                {intl.get("SignIn").d("Sign In")}
                                            </MuiLink>
                                        </Typography>
                                    </Box>
                                </form>
                            </>
                        )}
                    </CardContent>
                </Card>

                {/* Footer */}
                <Box sx={{ mt: 4, textAlign: "center" }}>
                    <Typography
                        variant="body2"
                        sx={{
                            color: APP_COLORS.neutral.gray500,
                        }}
                    >
                        {intl.get("CopyrightLena", { year: new Date().getFullYear() }).d("© {year} Lena. All rights reserved.")}
                    </Typography>
                </Box>
            </Box>
        </Container>
    );
};
